import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useAuthContext } from './auth-provider';
import { ThemeToggle } from '@/components/theme-toggle';

export function ResizableNavbar() {
  const [scrolled, setScrolled] = useState(false);
  // Simplify width behavior to avoid overflow and mask issues
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();
  const firstMobileItemRef = React.useRef<HTMLButtonElement | null>(null);
  const { user, signOut } = useAuthContext();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      setScrolled(y > 4);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Focus first mobile nav item when menu opens
  useEffect(() => {
    if (mobileOpen) {
      firstMobileItemRef.current?.focus();
    }
  }, [mobileOpen]);

  // Top-level navigation per request
  const topNav = [
    { label: 'Home', link: '/' },
    { label: 'Pricing', link: '/pricing' },
    { label: 'Contact', link: '/contact' },
    { label: 'FAQ', link: '/faq' },
  ];

  // Items under "More" dropdown
  const moreNav = [
    { label: 'Templates', link: '/templates' },
    { label: 'Blog', link: '/blog' },
    { label: 'Docs', link: '/docs' },
    { label: 'Tutorial', link: '/tutorial' },
    { label: 'Supported Nodes', link: '/supported-nodes' },
    { label: 'Deployment Guide', link: '/deployment-guide' },
  ];

  const go = (to: string) => {
    if (to.startsWith('#')) {
      const id = to.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // fallback to home with hash
      navigate('/' + to);
    } else {
      navigate(to);
    }
  };

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300`}>
      <div className="mx-auto max-w-[1200px] w-full px-2 md:px-4">
        <nav
          className={`bg-background/80 backdrop-blur transition-all duration-300 ${scrolled ? 'm-2 border border-border rounded-lg shadow-lg' : 'm-0 shadow-none'}`}
          aria-label="Primary"
        >
          <div className="px-3 h-14 flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/favicon-32.png" alt="n8n2py" className="h-6 w-6" />
              <span className="text-sm font-semibold tracking-wide">n8n2py</span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-2 relative">
              {topNav.map((it) => (
                <button
                  key={it.label}
                  onClick={() => go(it.link)}
                  className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground rounded-md transition-colors"
                >
                  {it.label}
                </button>
              ))}
              {/* More dropdown */}
              <div className="relative">
                <button
                  onClick={() => setMoreOpen((v) => !v)}
                  onMouseEnter={() => setMoreOpen(true)}
                  onMouseLeave={() => setMoreOpen(false)}
                  className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground rounded-md transition-colors inline-flex items-center gap-1"
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                >
                  More <ChevronDown className="h-3 w-3" />
                </button>
                {moreOpen && (
                  <div
                    onMouseEnter={() => setMoreOpen(true)}
                    onMouseLeave={() => setMoreOpen(false)}
                    className="absolute mt-2 right-0 w-56 border border-border rounded-md bg-background shadow-md z-50"
                    role="menu"
                    aria-label="More"
                  >
                    <div className="py-1">
                      {moreNav.map((it) => (
                        <button
                          key={it.label}
                          onClick={() => { setMoreOpen(false); go(it.link); }}
                          role="menuitem"
                          className="w-full text-left px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-muted/40"
                        >
                          {it.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-2">
              <ThemeToggle />
              {!user ? (
                <>
                  <Button variant="outline" size="sm" className="text-xs uppercase tracking-wider" onClick={() => navigate('/login')}>
                    Login
                  </Button>
                  <Button
                    size="sm"
                    className="text-xs uppercase tracking-wider"
                    onClick={() => navigate('/login?signup=1&redirect=/upload')}
                  >
                    Sign Up
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs uppercase tracking-wider"
                    onClick={handleSignOut}
                  >
                    Logout
                  </Button>
                  <Button
                    size="sm"
                    className="text-xs uppercase tracking-wider"
                    onClick={() => navigate('/upload')}
                  >
                    Go to Upload
                  </Button>
                </>
              )}
            </div>

            {/* Mobile toggle */}
            <button
              className="md:hidden p-2"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileOpen && (
            <div id="mobile-nav" className="md:hidden border-t border-border px-3 py-2" role="menu" aria-label="Mobile navigation">
              <div className="flex flex-col gap-1 py-2">
                {topNav.map((it, idx) => (
                  <button
                    key={it.label}
                    ref={idx === 0 ? firstMobileItemRef : undefined}
                    onClick={() => { setMobileOpen(false); go(it.link); }}
                    role="menuitem"
                    className="text-sm text-left px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  >
                    {it.label}
                  </button>
                ))}
                <div className="mt-2 px-3 pt-2 text-xs uppercase tracking-wider text-muted-foreground">More</div>
                {moreNav.map((it) => (
                  <button
                    key={it.label}
                    onClick={() => { setMobileOpen(false); go(it.link); }}
                    role="menuitem"
                    className="text-sm text-left px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  >
                    {it.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2 pt-2">
                <div className="flex justify-end">
                  <ThemeToggle />
                </div>
                {!user ? (
                  <>
                    <Button variant="outline" className="w-full" onClick={() => { setMobileOpen(false); navigate('/login'); }} aria-label="Login">
                      Login
                    </Button>
                    <Button className="w-full" onClick={() => { setMobileOpen(false); navigate('/login?signup=1&redirect=/upload'); }} aria-label="Get started">
                      Sign Up
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="w-full" onClick={() => { setMobileOpen(false); handleSignOut(); }} aria-label="Logout">
                      Logout
                    </Button>
                    <Button className="w-full" onClick={() => { setMobileOpen(false); navigate('/upload'); }} aria-label="Go to Upload">
                      Go to Upload
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
}
